import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, BookOpen, Trash2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { KB_TEMPLATES } from "@/lib/kbTemplates";

const TEMPLATE_GROUPS = [...new Set(KB_TEMPLATES.map((t) => t.group))];

const TemplatePicker = ({ onPick }) => (
  <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm" data-testid="kb-template-picker">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Start from a template</div>
    {TEMPLATE_GROUPS.map((g) => (
      <div key={g} className="mb-2 last:mb-0">
        <div className="text-[11px] font-medium text-red-600 mb-1">{g}</div>
        <div className="flex flex-wrap gap-1.5">
          {KB_TEMPLATES.filter((t) => t.group === g).map((t) => (
            <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`kb-template-${t.id}`} className="inline-flex items-center gap-1 px-2 h-7 text-xs border border-slate-200 bg-white hover:border-red-400 hover:text-red-600 rounded-sm transition-colors">
              <FileText className="h-3 w-3" strokeWidth={1.75} /> {t.name}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "General", tags: "" });
  const [cat, setCat] = useState("All");
  const categories = ["All", ...[...new Set(articles.map((a) => a.category))].sort()];
  const shown = cat === "All" ? articles : articles.filter((a) => a.category === cat);

  const applyTemplate = (tpl) => {
    if (form.content.trim() && !window.confirm("Replace the current content with this template?")) { return; }
    setForm((f) => ({ ...f, content: tpl.content, category: tpl.group, tags: tpl.tags, title: !f.title || KB_TEMPLATES.some((x) => x.name === f.title) ? tpl.name : f.title }));
  };

  const load = async () => {
    const { data } = await api.get("/kb", { params: q ? { q } : {} });
    setArticles(data);
  };
  useEffect(() => { load(); }, [q]);

  const canWrite = user?.role === "agent" || user?.role === "admin";

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content required"); return;
    }
    await api.post("/kb", {
      title: form.title,
      content: form.content,
      category: form.category || "General",
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
    toast.success("Article published");
    setForm({ title: "", content: "", category: "General", tags: "" });
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/kb/${id}`);
    setSelected(null);
    load();
    toast.success("Deleted");
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden border border-slate-200">
        <img
          src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNlcnZlciUyMGRhdGElMjBjbG91ZHxlbnwwfHx8fDE3ODQyODI0ODZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Knowledge base"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-950/40" />
        <div className="relative p-10 lg:p-16">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">Knowledge Base</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3 text-white" data-testid="kb-heading">Find an answer, fast.</h1>
          <p className="text-slate-300 mt-3 max-w-lg text-sm">Search articles, guides, and IT how-tos before opening a ticket.</p>
          <div className="mt-6 max-w-xl relative">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" strokeWidth={1.5} />
            <Input
              data-testid="kb-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search VPN, laptop, password reset..."
              className="pl-11 bg-white/80 backdrop-blur border-slate-200 rounded-sm h-12 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500" data-testid="kb-count">{shown.length} Articles</div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="kb-new-btn"><Plus className="h-4 w-4" strokeWidth={2}/> New Article</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">New Article</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <TemplatePicker onPick={applyTemplate} />
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="kb-title" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="kb-category" />
                  <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-white border-slate-200 rounded-sm" data-testid="kb-tags" />
                </div>
                <Textarea placeholder="Content" rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-white border-slate-200 rounded-sm font-mono text-xs" data-testid="kb-content" />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submit} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="kb-publish-btn">Publish</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="kb-category-filter">
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`kb-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`px-3 h-8 text-xs rounded-sm border transition-colors ${cat === c ? "bg-red-600 border-red-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-red-400"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 border border-slate-200 bg-white divide-y divide-slate-800 max-h-[70vh] overflow-y-auto">
          {shown.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="kb-empty">No articles found.</div>
          )}
          {shown.map((a) => (
            <button
              key={a.article_id}
              data-testid={`kb-item-${a.article_id}`}
              onClick={() => setSelected(a)}
              className={`w-full text-left p-4 hover:bg-slate-100 ${selected?.article_id === a.article_id ? "bg-white" : ""}`}
            >
              <div className="text-sm font-medium">{a.title}</div>
              <div className="text-[11px] text-slate-500 mt-1 font-mono uppercase tracking-wider">{a.category}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 border border-slate-200 bg-white p-8 min-h-[300px]">
          {selected ? (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-600">{selected.category}</div>
                  <h2 className="font-display text-3xl font-black tracking-tight mt-2" data-testid="kb-article-title">{selected.title}</h2>
                </div>
                {canWrite && (
                  <button onClick={() => del(selected.article_id)} className="text-slate-500 hover:text-red-400" data-testid="kb-delete-btn">
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(selected.tags || []).map((t) => (
                  <span key={t} className="text-[10px] font-mono uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 rounded-sm">{t}</span>
                ))}
              </div>
              <div className="mt-6 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed break-words" data-testid="kb-article-content">{selected.content}</div>
              <div className="mt-6 text-xs text-slate-500 font-mono">By {selected.author_name} · {new Date(selected.updated_at).toLocaleString()}</div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-500">
              <BookOpen className="h-8 w-8 mb-3" strokeWidth={1.2} />
              <div className="text-sm">Select an article to read.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
